import { motion } from "framer-motion";
import { Activity, BarChart3, TrendingUp, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { fadeIn, fadeInUp, stagger } from "../../lib/motion";
import { useAppStore } from "../../store/useAppStore";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { PageHeader } from "../ui/page-header";

function useCountUp(target: number, duration = 750): number {
  const [count, setCount] = useState(0);
  const raf = useRef<number>(0);

  useEffect(() => {
    if (target === 0) { setCount(0); return; }
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      // easeOutQuart
      setCount(Math.round((1 - Math.pow(1 - p, 4)) * target));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);

  return count;
}

export function AnalyticsView() {
  const { projects, deployments } = useAppStore();
  const [analytics, setAnalytics] = useState({
    totalDeployments: 0,
    activeProjects: 0,
    successRate: 0,
    avgBuildTime: "0s",
  });

  useEffect(() => {
    const activeProjects = projects.length;
    const totalDeployments = deployments.length;
    const successfulDeployments = deployments.filter(
      (d) => d.status === "running"
    ).length;
    const successRate =
      totalDeployments > 0
        ? Math.round((successfulDeployments / totalDeployments) * 100)
        : 0;

    setAnalytics({
      totalDeployments,
      activeProjects,
      successRate,
      avgBuildTime: totalDeployments > 0 ? "N/A" : "—",
    });
  }, [projects, deployments]);

  const deploymentsCount = useCountUp(analytics.totalDeployments);
  const projectsCount = useCountUp(analytics.activeProjects);
  const successRateCount = useCountUp(analytics.successRate);

  const stats = [
    {
      label: "Total Deployments",
      value: deploymentsCount,
      suffix: "",
      sub: "All time deployments",
      icon: BarChart3,
    },
    {
      label: "Active Projects",
      value: projectsCount,
      suffix: "",
      sub: "Total projects",
      icon: TrendingUp,
    },
    {
      label: "Success Rate",
      value: successRateCount,
      suffix: "%",
      sub: "Deployment success",
      icon: Users,
    },
    {
      label: "Build Time",
      value: analytics.avgBuildTime,
      suffix: "",
      sub: "Average build time",
      icon: Activity,
      raw: true,
    },
  ];

  return (
    <div className="space-y-6">
      <motion.div variants={fadeIn} initial="hidden" animate="visible">
        <PageHeader
          eyebrow="Observability"
          title="Analytics"
          description="Monitor your application performance and usage metrics"
        />
      </motion.div>

      <motion.div
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
        variants={stagger}
        initial="hidden"
        animate="visible"
      >
        {stats.map(({ label, value, suffix, sub, icon: Icon, raw }) => (
          <motion.div key={label} variants={fadeInUp}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{label}</CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-h2">
                  {raw ? value : `${value}${suffix}`}
                </div>
                <p className="text-xs text-muted-foreground">{sub}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {analytics.totalDeployments === 0 && analytics.activeProjects === 0 && (
        <motion.div
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          className="py-12 text-center"
        >
          <BarChart3 className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-2 text-h4">No analytics data yet</h3>
          <p className="mx-auto max-w-sm text-muted-foreground">
            Analytics and metrics will appear here once you create projects and
            deployments.
          </p>
        </motion.div>
      )}
    </div>
  );
}
